import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageUpload from '../Assets/ImageUpload';
import FormActionButtons from '../Assets/FormActionButtons';
import LanguageTabs, { FormLang } from '../Assets/LanguageTabs';
import { useTranslation } from 'react-i18next';

type Multi = { en: string; mk: string; sq: string };

interface ContactInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (entry: {
        name: Multi;
        description: Multi;
        image?: File;
    }) => void;
}

const ContactInfoModal: React.FC<ContactInfoModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const [activeLang, setActiveLang] = useState<FormLang>('en');
    const [name, setName] = useState<Multi>({ en: '', mk: '', sq: '' });
    const [description, setDescription] = useState<Multi>({ en: '', mk: '', sq: '' });
    const [image, setImage] = useState<File | null>(null);

    const setField = (
        setter: React.Dispatch<React.SetStateAction<Multi>>,
        value: string
    ) => setter((prev) => ({ ...prev, [activeLang]: value }));

    const isQuillEmpty = (value: string) => value.replace(/<(.|\n)*?>/g, '').trim() === '';

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({
            name,
            description,
            image: image || undefined,
        });
        resetForm();
        onClose();
    };

    const resetForm = () => {
        setName({ en: '', mk: '', sq: '' });
        setDescription({ en: '', mk: '', sq: '' });
        setImage(null);
        setActiveLang('en');
    };

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    const langSuffix = activeLang.toUpperCase();

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-semibold mb-6 text-blue-900 text-center">
                    {t(`forms.createContact`)}
                </h2>

                <LanguageTabs active={activeLang} onChange={setActiveLang} />

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">{t('forms.name')} ({langSuffix})</label>
                        <input
                            type="text"
                            value={name[activeLang]}
                            onChange={(e) => setField(setName, e.target.value)}
                            className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required={activeLang !== 'sq'}
                        />
                    </div>
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">{t('forms.description')} ({langSuffix})</label>
                        <ReactQuill
                            key={`desc-${activeLang}`}
                            value={description[activeLang]}
                            onChange={(value) => setField(setDescription, value)}
                            theme="snow"
                            className="h-48"
                        />
                    </div>
                    <br />
                    <br />
                    <ImageUpload onImageSelect={setImage} />
                    <FormActionButtons
                        edit={false}
                        onClose={handleCancel}
                        onSubmit={handleSubmit}
                        disabled={
                            !name.en.trim() ||
                            !name.mk.trim() ||
                            isQuillEmpty(description.en) ||
                            isQuillEmpty(description.mk)
                        }
                    />
                </form>
            </div>
        </div>
    );
};

export default ContactInfoModal;
